import { Check } from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";
import { scrollToId } from "./Nav";

const PLANS = [
  {
    name: "Founding Practice",
    chip: "Limited spots",
    featured: true,
    price: "Free for 30 days",
    priceNote: "Locked-in discounted rate for life",
    description:
      "For our first practices. We build and refine the system together, by hand where needed, in exchange for feedback and permission to use real results as a case study once they exist.",
    features: [
      "Up to 2 channels connected",
      "Full response & follow-up service",
      "No setup fee",
      "No commitment, cancel anytime",
    ],
    cta: "Apply as a Founding Practice",
    testid: "pricing-apply-founding-btn",
  },
  {
    name: "Starter",
    price: "£295/month",
    priceNote: "£450 one-off setup",
    description: "For single-location practices with moderate enquiry volume.",
    features: [
      "Up to 2 channels (e.g. website + phone)",
      "Up to ~150 enquiries/month",
      "New enquiry response, follow-up, booking, reminders",
      "Month-to-month, no lock-in",
    ],
    cta: "Book a Demo",
    testid: "pricing-book-demo-starter-btn",
  },
  {
    name: "Growth",
    price: "£595/month",
    priceNote: "£650 one-off setup",
    description:
      "For higher-volume practices or those focused on higher-value treatments like implants and Invisalign.",
    features: [
      "All channels (web, phone, WhatsApp, email, social)",
      "Up to ~400 enquiries/month",
      "Everything in Starter, plus lead reactivation and full reporting",
      "Month-to-month, no lock-in",
    ],
    cta: "Book a Demo",
    testid: "pricing-book-demo-growth-btn",
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="scroll-mt-24 border-t border-ink/10 bg-sand/50 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <Reveal>
            <Chapter number="09" label="Pricing" />
          </Reveal>
          <Reveal delay={60}>
            <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Pricing
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-base leading-relaxed text-ink/70 md:text-lg">
              Flat, transparent pricing — no commission on treatment value or
              per-patient fees.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 120} className="h-full">
              <div
                data-testid={`pricing-card-${i}`}
                className={`relative flex h-full flex-col rounded-2xl bg-white p-8 ${
                  plan.featured
                    ? "border-2 border-teal shadow-[0_10px_36px_rgba(14,90,84,0.10)]"
                    : "border border-ink/10"
                }`}
              >
                {plan.chip && (
                  <span
                    data-testid="pricing-founding-chip"
                    className="absolute -top-3.5 left-8 rounded-full bg-teal px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white"
                  >
                    {plan.chip}
                  </span>
                )}
                <h3 className="font-display text-xl font-medium text-ink">{plan.name}</h3>
                <p
                  data-testid={`pricing-price-${i}`}
                  className="mt-5 font-display text-3xl font-medium tracking-tight text-teal"
                >
                  {plan.price}
                </p>
                <p className="mt-1.5 text-sm font-medium text-ink/60">{plan.priceNote}</p>
                <p className="mt-5 text-sm leading-relaxed text-ink/65">{plan.description}</p>
                <ul className="mt-7 flex flex-col gap-3.5 border-t border-ink/10 pt-7">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-soft">
                        <Check size={12} className="text-teal" strokeWidth={2.5} />
                      </span>
                      <span className="text-sm leading-relaxed text-ink/75">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={plan.testid}
                  onClick={() => scrollToId("contact")}
                  className={`mt-8 w-full rounded-full px-6 py-3.5 text-sm font-medium tracking-wide transition-colors ${
                    plan.featured
                      ? "bg-teal text-white hover:bg-teal-dark"
                      : "border border-ink/25 text-ink hover:border-teal hover:text-teal"
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={150}>
          <p
            data-testid="pricing-multi-location-note"
            className="mt-12 text-center text-sm leading-relaxed text-ink/65 md:text-base"
          >
            Multiple locations? Pricing is quoted after a short call, based on
            your setup.{" "}
            <button
              data-testid="pricing-get-in-touch-link"
              onClick={() => scrollToId("contact")}
              className="font-medium text-teal underline decoration-teal/40 underline-offset-4 transition-colors hover:decoration-teal"
            >
              Get in touch
            </button>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
